import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { therapyService, CheckInData } from '../../services/therapyService';
import { 
  X, 
  Sparkles, 
  Wind, 
  Heart, 
  Brain, 
  Activity,
  Smile,
  Meh,
  Frown,
  BriefcaseIcon,
  Moon,
  Zap,
  Users,
  DollarSign,
  HeartPulse,
  Loader2
} from 'lucide-react';

interface DailyCheckInModalProps {
  onClose: () => void;
  onSuccess: (checkIn: any) => void;
}

const MOOD_OPTIONS = [
  {
    value: 'GREAT',
    label: 'Excelent',
    icon: Smile,
    gradient: 'from-green-400 to-emerald-500',
    shadow: 'shadow-green-500/50',
    color: 'text-green-500'
  },
  {
    value: 'GOOD',
    label: 'Bine',
    icon: Smile,
    gradient: 'from-blue-400 to-cyan-500',
    shadow: 'shadow-blue-500/50',
    color: 'text-blue-500'
  },
  {
    value: 'OKAY',
    label: 'Okay',
    icon: Meh,
    gradient: 'from-yellow-400 to-orange-400',
    shadow: 'shadow-yellow-500/50',
    color: 'text-yellow-500'
  },
  {
    value: 'BAD',
    label: 'Rău',
    icon: Frown,
    gradient: 'from-orange-400 to-red-500',
    shadow: 'shadow-orange-500/50',
    color: 'text-orange-500'
  },
  {
    value: 'TERRIBLE',
    label: 'Groaznic',
    icon: Frown,
    gradient: 'from-red-500 to-rose-600',
    shadow: 'shadow-red-500/50',
    color: 'text-red-500'
  }
];

const EMOTION_OPTIONS = [
  { value: 'anxios', label: 'Anxios', icon: Wind, color: 'text-purple-500 bg-purple-50 border-purple-200' },
  { value: 'trist', label: 'Trist', icon: Heart, color: 'text-blue-500 bg-blue-50 border-blue-200' },
  { value: 'obosit', label: 'Obosit', icon: Activity, color: 'text-gray-500 bg-gray-50 border-gray-200' },
  { value: 'stresat', label: 'Stresat', icon: Brain, color: 'text-red-500 bg-red-50 border-red-200' },
  { value: 'fericit', label: 'Fericit', icon: Sparkles, color: 'text-yellow-500 bg-yellow-50 border-yellow-200' },
  { value: 'calm', label: 'Calm', icon: Wind, color: 'text-green-500 bg-green-50 border-green-200' },
  { value: 'copleșit', label: 'Copleșit', icon: Zap, color: 'text-orange-500 bg-orange-50 border-orange-200' },
  { value: 'frustrat', label: 'Frustrat', icon: Activity, color: 'text-red-400 bg-red-50 border-red-200' },
];

const TRIGGER_OPTIONS = [
  { value: 'muncă', label: 'Muncă', icon: BriefcaseIcon, color: 'text-blue-600' },
  { value: 'somn', label: 'Somn', icon: Moon, color: 'text-indigo-600' },
  { value: 'durere', label: 'Durere', icon: Zap, color: 'text-red-600' },
  { value: 'relații', label: 'Relații', icon: Users, color: 'text-pink-600' },
  { value: 'financiar', label: 'Financiar', icon: DollarSign, color: 'text-green-600' },
  { value: 'sănătate', label: 'Sănătate', icon: HeartPulse, color: 'text-purple-600' },
];

export default function DailyCheckInModal({ onClose, onSuccess }: DailyCheckInModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [checkInResult, setCheckInResult] = useState<any>(null);

  const [formData, setFormData] = useState<CheckInData>({
    mood: 'OKAY',
    emotions: [],
    intensity: 5,
    triggers: [],
    notes: ''
  });

  const handleSubmit = async () => {
    if (formData.emotions.length === 0) {
      setError('Selectează cel puțin o emoție');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await therapyService.createCheckIn(formData);
      setCheckInResult(result);
      setShowRecommendations(true);
      onSuccess(result);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Eroare la salvarea check-in-ului');
      setLoading(false);
    }
  };

  if (showRecommendations && checkInResult) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-8 rounded-t-3xl text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">Check-in Salvat! ✅</h2>
            <p className="text-purple-100 text-sm">Starea ta a fost înregistrată cu succes</p>
          </div>

          <div className="p-6 space-y-4">
            {/* AI generating notice */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Recomandări AI în pregătire</h3>
                <p className="text-sm text-gray-600">
                  AI-ul generează recomandări personalizate pentru tine. 
                  Revino pe pagina de Jurnal Emoțional în câteva secunde pentru a le vedea.
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
            >
              Gata! 🎉
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <Heart className="w-5 h-5 text-white" />
                </span>
                Check-in Zilnic
              </h2>
              <p className="text-purple-100 mt-1 text-sm">Cum te simți astăzi?</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-all"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Progress */}
          <div className="mt-4 flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-sm text-red-700">
              {error.toLowerCase().includes('insufficient credits') || error.toLowerCase().includes('credite insuficiente')
                ? 'Credite insuficiente pentru a finaliza check-in-ul.'
                : error}
            </p>
            {(error.toLowerCase().includes('insufficient credits') || error.toLowerCase().includes('credite insuficiente')) ? (
              <button
                onClick={() => { onClose(); navigate('/extra-credits'); }}
                className="mt-3 w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:shadow-md transition-all text-sm"
              >
                Cumpără credite
              </button>
            ) : null}
          </div>
        )}

        <div className="p-6">
          {/* Step 1: Mood */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Alege mood-ul tău</h3>
                <div className="grid grid-cols-5 gap-3">
                  {MOOD_OPTIONS.map((mood) => {
                    const IconComponent = mood.icon;
                    return (
                      <button
                        key={mood.value}
                        onClick={() => setFormData({ ...formData, mood: mood.value as any })}
                        className={`relative group transition-all ${
                          formData.mood === mood.value
                            ? `bg-gradient-to-br ${mood.gradient} shadow-xl ${mood.shadow} scale-110 text-white`
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        } rounded-2xl p-4 flex flex-col items-center gap-2 border-2 ${
                          formData.mood === mood.value ? 'border-transparent' : 'border-gray-200'
                        }`}
                      >
                        <IconComponent className="w-10 h-10" />
                        <span className={`text-xs font-medium`}>
                          {mood.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
              >
                Continuă →
              </button>
            </div>
          )}

          {/* Step 2: Emotions & Intensity */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Ce emoții simți?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {EMOTION_OPTIONS.map((emotion) => {
                    const IconComponent = emotion.icon;
                    const isSelected = formData.emotions.includes(emotion.value);
                    return (
                      <button
                        key={emotion.value}
                        onClick={() => {
                          if (isSelected) {
                            setFormData({
                              ...formData,
                              emotions: formData.emotions.filter(e => e !== emotion.value)
                            });
                          } else {
                            setFormData({
                              ...formData,
                              emotions: [...formData.emotions, emotion.value]
                            });
                          }
                        }}
                        className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                          isSelected
                            ? `${emotion.color} shadow-lg scale-105`
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className="w-5 h-5" />
                        <span className="font-medium text-sm">{emotion.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Intensitate: <span className="text-2xl font-bold text-purple-600">{formData.intensity}</span>/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={formData.intensity}
                  onChange={(e) => setFormData({ ...formData, intensity: parseInt(e.target.value) })}
                  className="w-full h-3 bg-gradient-to-r from-green-300 via-yellow-300 to-red-500 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, 
                      #86efac 0%, 
                      #fde047 50%, 
                      #f87171 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Ușor</span>
                  <span>Intens</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
                >
                  ← Înapoi
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg"
                >
                  Continuă →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Triggers & Notes */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Ce a declanșat? (opțional)</h3>
                <div className="grid grid-cols-2 gap-3">
                  {TRIGGER_OPTIONS.map((trigger) => {
                    const IconComponent = trigger.icon;
                    const isSelected = formData.triggers?.includes(trigger.value);
                    return (
                      <button
                        key={trigger.value}
                        onClick={() => {
                          const triggers = formData.triggers || [];
                          if (isSelected) {
                            setFormData({
                              ...formData,
                              triggers: triggers.filter(t => t !== trigger.value)
                            });
                          } else {
                            setFormData({
                              ...formData,
                              triggers: [...triggers, trigger.value]
                            });
                          }
                        }}
                        className={`p-3 rounded-xl border-2 transition-all text-sm font-medium flex items-center gap-2 ${
                          isSelected
                            ? 'bg-purple-50 border-purple-500 text-purple-700 shadow-lg'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className={`w-5 h-5 ${isSelected ? trigger.color : 'text-gray-400'}`} />
                        {trigger.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Note (opțional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Detalii suplimentare despre cum te simți..."
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
                >
                  ← Înapoi
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Se salvează...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Finalizează Check-in
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
